import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Send, Inbox as InboxIcon, AlertTriangle, Archive } from 'lucide-react';
import {
  apiGetInbox, apiInboxStats, apiGetInboxMessage,
  apiSyncInbox, apiReplyToInbox, apiArchiveInboxMessage,
} from '../../api';
import { Stat, StatusBadge, Empty, formatDateTime, relativeTime } from './parts';
import styles from './Mailer.module.css';

const FILTERS = [
  { key: 'reply', label: 'Replies' },
  { key: 'out_of_office', label: 'Out of office' },
  { key: 'bounce', label: 'Bounces' },
  { key: 'unsubscribe_request', label: 'Opt-outs' },
  { key: 'all', label: 'Everything' },
];

/**
 * The unified inbox: every inbound message the sync matched back to a contact,
 * with the whole conversation and a reply box, so a reply never has to be
 * chased down in Zoho.
 */
const InboxTab = ({ jwtToken, onUnreadChange }) => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [filter, setFilter] = useState('reply');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inbox, statData] = await Promise.all([
        apiGetInbox(jwtToken, { classification: filter, limit: 100 }),
        apiInboxStats(jwtToken),
      ]);
      setMessages(inbox.messages || []);
      setStats(statData);
      onUnreadChange?.(statData.unread || 0);
    } catch (e) {
      setError(e.message || 'Could not load the inbox');
    } finally {
      setLoading(false);
    }
  }, [jwtToken, filter, onUnreadChange]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    setError('');
    try {
      const result = await apiSyncInbox(jwtToken);
      setNotice(
        result.ingested
          ? `Found ${result.ingested} new message${result.ingested === 1 ? '' : 's'}`
          : 'No new messages'
      );
      setTimeout(() => setNotice(''), 4000);
      await load();
    } catch (e) {
      setError(e.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const open = async (message) => {
    setError('');
    setReplyBody('');
    try {
      const detail = await apiGetInboxMessage(jwtToken, message._id);
      setSelected(detail.message);
      setThread(detail.thread || []);
      // Opening marks it read server-side; mirror that locally.
      setMessages((list) => list.map((m) => (m._id === message._id ? { ...m, isRead: true } : m)));
      const statData = await apiInboxStats(jwtToken);
      setStats(statData);
      onUnreadChange?.(statData.unread || 0);
    } catch (e) {
      setError(e.message || 'Could not open that message');
    }
  };

  const sendReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    setError('');
    try {
      await apiReplyToInbox(jwtToken, selected._id, { body: replyBody });
      setReplyBody('');
      setNotice('Reply sent');
      setTimeout(() => setNotice(''), 4000);
      await open(selected);
    } catch (e) {
      setError(e.message || 'Could not send the reply');
    } finally {
      setSending(false);
    }
  };

  const archive = async () => {
    try {
      await apiArchiveInboxMessage(jwtToken, selected._id, true);
      setSelected(null);
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      {stats && (
        <div className={styles.statGrid}>
          <Stat value={stats.replies} label="Replies" tone="success" />
          <Stat value={stats.unread} label="Unread" tone={stats.unread ? 'success' : undefined} />
          <Stat value={stats.outOfOffice} label="Out of office" />
          <Stat value={stats.bounces} label="Bounces" tone={stats.bounces ? 'danger' : undefined} />
        </div>
      )}

      {error && <div className={styles.dangerBox}>{error}</div>}
      {notice && <div className={styles.infoBox}>{notice}</div>}

      {stats?.lastSyncError && (
        <div className={styles.warnBox}>
          <div className={styles.boxTitle}><AlertTriangle size={16} /> Reply sync is failing</div>
          {stats.lastSyncError}
        </div>
      )}

      <div className={styles.toolbar}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={filter === f.key ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            onClick={() => { setFilter(f.key); setSelected(null); }}
          >
            {f.label}
          </button>
        ))}
        <div className={styles.spacer} />
        <span className="text-sm text-muted">
          {stats?.lastSyncAt ? `Synced ${relativeTime(stats.lastSyncAt)} · via ${stats.backend}` : 'Not synced yet'}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={sync} disabled={syncing}>
          <RefreshCw size={15} /> {syncing ? 'Checking…' : 'Check now'}
        </button>
      </div>

      {loading ? (
        <div className="empty"><span className="spinner" style={{ width: 30, height: 30 }} /></div>
      ) : messages.length === 0 ? (
        <Empty icon={<InboxIcon size={40} color="var(--text-muted)" />} title="Nothing here yet">
          Replies to your campaigns land here automatically. Bounces and out-of-office messages are
          filed separately — an autoresponder never counts as a reply, so it will not stop a sequence.
        </Empty>
      ) : (
        <div className={styles.splitPane}>
          <div className={styles.paneList}>
            {messages.map((m) => (
              <button
                key={m._id}
                className={`${styles.paneItem} ${selected?._id === m._id ? styles.paneItemActive : ''} ${!m.isRead ? styles.paneItemUnread : ''}`}
                onClick={() => open(m)}
              >
                <div className={styles.paneItemTitle}>
                  {m.contact
                    ? [m.contact.firstName, m.contact.lastName].filter(Boolean).join(' ') || m.fromAddress
                    : m.fromAddress}
                </div>
                <div className={styles.paneItemMeta}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.campaign?.name || m.fromAddress}
                  </span>
                  <span style={{ flexShrink: 0 }}>{relativeTime(m.receivedAt)}</span>
                </div>
                {m.snippet && <div className={styles.paneItemSnippet}>{m.snippet}</div>}
              </button>
            ))}
          </div>

          <div>
            {!selected ? (
              <Empty icon={<InboxIcon size={36} color="var(--text-muted)" />} title="Select a message" />
            ) : (
              <div className="card">
                <div className="page-header" style={{ marginBottom: 16 }}>
                  <div style={{ minWidth: 0 }}>
                    <h3 className="card-title" style={{ marginBottom: 4 }}>{selected.subject || '(no subject)'}</h3>
                    <div className="text-sm text-muted">
                      {selected.fromAddress} · {formatDateTime(selected.receivedAt)}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      <StatusBadge status={selected.contact?.status} />
                      {selected.classification !== 'reply' && (
                        <span className="badge badge-medium">{selected.classification.replace(/_/g, ' ')}</span>
                      )}
                      {selected.campaign && <span className="badge badge-high">{selected.campaign.name}</span>}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={archive} title="Archive">
                    <Archive size={15} />
                  </button>
                </div>

                {selected.classification === 'reply' && selected.contact?.status === 'replied' && (
                  <div className={styles.infoBox}>
                    This reply stopped the sequence automatically — no further follow-ups will go to
                    this contact.
                  </div>
                )}

                <div style={{ maxHeight: '46vh', overflowY: 'auto', marginBottom: 16 }}>
                  {thread.map((entry, i) => (
                    <div
                      key={i}
                      className={`${styles.threadMessage} ${entry.direction === 'out' ? styles.threadOut : styles.threadIn}`}
                    >
                      <div className={styles.threadMeta}>
                        {entry.direction === 'out'
                          ? `You · ${entry.stepIndex < 0 ? 'reply' : `step ${entry.stepIndex + 1}`}`
                          : 'Them'} · {formatDateTime(entry.at)}
                      </div>
                      {entry.body || <em style={{ opacity: 0.6 }}>(no text content)</em>}
                    </div>
                  ))}
                </div>

                {selected.classification === 'reply' && (
                  <div>
                    <textarea
                      className={`input ${styles.textarea}`}
                      style={{ minHeight: 120 }}
                      placeholder={`Reply to ${selected.fromAddress}…`}
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                    />
                    <div className={styles.toolbar} style={{ marginTop: 12, marginBottom: 0 }}>
                      <button className="btn btn-primary" onClick={sendReply} disabled={sending || !replyBody.trim()}>
                        <Send size={16} /> {sending ? 'Sending…' : 'Send reply'}
                      </button>
                      <span className="text-sm text-muted">
                        Threads onto their message. No unsubscribe footer — this is a conversation now.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InboxTab;
